import { useEffect, useRef, useState } from "react";
import type { EngagementProviderBootInput } from "@/provider/ProviderTypes";
import { debugLog } from "@/utils/debug";
import { CaptainUpProvider } from "@/providers/captainup/CaptainUpProvider";
import { bootCaptainUpProviderOrThrow } from "@/providers/captainup/CaptainUpBootWorker";

export type CaptainUpBootState = {
	captainUpProvider: CaptainUpProvider | null;
	captainUpReady: boolean;
	activeBootGeneration: number;
	readyBootGeneration: number | null;
	inFlightBootGeneration: number | null;
	lastBootError: Error | null;
};

function createIdleBootState(
	requestedBootGeneration: number
): CaptainUpBootState {
	return {
		captainUpProvider: null,
		captainUpReady: false,
		activeBootGeneration: requestedBootGeneration,
		readyBootGeneration: null,
		inFlightBootGeneration: null,
		lastBootError: null,
	};
}

function createBootingState(
	requestedBootGeneration: number
): CaptainUpBootState {
	return {
		captainUpProvider: null,
		captainUpReady: false,
		activeBootGeneration: requestedBootGeneration,
		readyBootGeneration: null,
		inFlightBootGeneration: requestedBootGeneration,
		lastBootError: null,
	};
}

function createReadyState(
	requestedBootGeneration: number,
	provider: CaptainUpProvider
): CaptainUpBootState {
	return {
		captainUpProvider: provider,
		captainUpReady: true,
		activeBootGeneration: requestedBootGeneration,
		readyBootGeneration: requestedBootGeneration,
		inFlightBootGeneration: null,
		lastBootError: null,
	};
}

function createFailedState(
	requestedBootGeneration: number,
	error: Error
): CaptainUpBootState {
	return {
		captainUpProvider: null,
		captainUpReady: false,
		activeBootGeneration: requestedBootGeneration,
		readyBootGeneration: null,
		inFlightBootGeneration: null,
		lastBootError: error,
	};
}

function buildBootKey(
	bootInput: EngagementProviderBootInput,
	requestedBootGeneration: number
): string {
	return [
		"captainup",
		bootInput.sessionKey,
		bootInput.serverUrl,
		bootInput.gameName,
		bootInput.lang,
		bootInput.operator,
		bootInput.platform,
		String(requestedBootGeneration),
	].join("::");
}

export function useCaptainUpBoot(
	bootEnabled: boolean,
	bootInput: EngagementProviderBootInput | null,
	requestedBootGeneration = 0
): CaptainUpBootState {
	const [state, setState] = useState<CaptainUpBootState>(() =>
		createIdleBootState(requestedBootGeneration)
	);

	const lastBootKeyRef = useRef<string | null>(null);
	const bootAttemptIdRef = useRef(0);

	useEffect(() => {
		debugLog("[EF CaptainUpBoot] hook state snapshot", {
			bootEnabled,
			hasBootInput: !!bootInput,
			requestedBootGeneration,
			activeBootGeneration: state.activeBootGeneration,
			readyBootGeneration: state.readyBootGeneration,
			inFlightBootGeneration: state.inFlightBootGeneration,
			captainUpReady: state.captainUpReady,
			hasProvider: !!state.captainUpProvider,
			lastBootError: state.lastBootError?.message ?? null,
		});
	}, [bootEnabled, bootInput, requestedBootGeneration, state]);

	useEffect(() => {
		if (!bootEnabled || !bootInput) {
			debugLog(
				"[EF CaptainUpBoot] resetting local boot state because prerequisites are absent",
				{
					bootEnabled,
					hasBootInput: !!bootInput,
					requestedBootGeneration,
				}
			);

			lastBootKeyRef.current = null;
			bootAttemptIdRef.current += 1;
			setState(createIdleBootState(requestedBootGeneration));
			return;
		}

		const bootKey = buildBootKey(bootInput, requestedBootGeneration);

		debugLog("[EF CaptainUpBoot] boot effect entered", {
			bootKey,
			requestedBootGeneration,
			currentActiveBootGeneration: state.activeBootGeneration,
			currentReadyBootGeneration: state.readyBootGeneration,
			currentInFlightBootGeneration: state.inFlightBootGeneration,
		});

		if (lastBootKeyRef.current === bootKey) {
			debugLog(
				"[EF CaptainUpBoot] boot effect ignored because bootKey is unchanged",
				{
					bootKey,
				}
			);
			return;
		}

		lastBootKeyRef.current = bootKey;
		bootAttemptIdRef.current += 1;

		const bootAttemptId = bootAttemptIdRef.current;

		debugLog("[EF CaptainUpBoot] starting new boot attempt", {
			bootKey,
			bootAttemptId,
			requestedBootGeneration,
		});

		setState(createBootingState(requestedBootGeneration));

		void (async () => {
			try {
				const nextProvider = await bootCaptainUpProviderOrThrow(bootInput);

				if (bootAttemptId !== bootAttemptIdRef.current) {
					debugLog(
						"[EF CaptainUpBoot] boot result ignored because a newer boot attempt exists",
						{
							bootAttemptId,
							currentBootAttemptId: bootAttemptIdRef.current,
							requestedBootGeneration,
						}
					);
					return;
				}

				setState(createReadyState(requestedBootGeneration, nextProvider));

				debugLog("[EF CaptainUpBoot] boot complete", {
					featureProvider: "captainup",
					requestedBootGeneration,
					bootAttemptId,
					hasProvider: true,
					captainUpReady: true,
					readyBootGeneration: requestedBootGeneration,
				});
			} catch (error) {
				if (bootAttemptId !== bootAttemptIdRef.current) {
					debugLog(
						"[EF CaptainUpBoot] boot failure ignored because a newer boot attempt exists",
						{
							bootAttemptId,
							currentBootAttemptId: bootAttemptIdRef.current,
							requestedBootGeneration,
						}
					);
					return;
				}

				const normalizedError =
					error instanceof Error ? error : new Error(String(error));

				setState(createFailedState(requestedBootGeneration, normalizedError));

				debugLog("[EF CaptainUpBoot] boot failed and local state was reset", {
					requestedBootGeneration,
					bootAttemptId,
					hasProvider: false,
					captainUpReady: false,
					readyBootGeneration: null,
					inFlightBootGeneration: null,
					lastBootError: normalizedError.message,
				});
			}
		})();
	}, [bootEnabled, bootInput, requestedBootGeneration]);

	return state;
}